
import React from 'react';
import { Menu } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from '@/components/ui/sheet';
import { Separator } from '@/components/ui/separator';
import { MobileMenuProvider, useMobileMenu } from './MobileMenuContext';
import MobileUserProfile from './MobileUserProfile';
import MobileNavigationItems from './MobileNavigationItems';
import MobileUserMenuItems from './MobileUserMenuItems';
import MobileAdminMenuItems from './MobileAdminMenuItems';
import MobileAuthSection from './MobileAuthSection';
import MobileDashboardButton from './MobileDashboardButton';

interface MobileMenuProps {
  user: any;
  signOut: () => Promise<void>;
  getUserInitials: () => string;
}

const MobileMenuContent = ({ user, signOut, getUserInitials }: MobileMenuProps) => {
  const { isOpen, setIsOpen, isAdmin } = useMobileMenu();

  return (
    <Sheet open={isOpen} onOpenChange={setIsOpen}>
      <SheetTrigger asChild>
        <Button variant="ghost" size="icon" className="h-8 w-8">
          <Menu className="h-5 w-5" />
          <span className="sr-only">Toggle menu</span>
        </Button>
      </SheetTrigger>
      <SheetContent 
        side="right" 
        className="w-80 p-0 overflow-y-auto overscroll-contain"
        style={{ WebkitOverflowScrolling: 'touch' }}
      >
        <SheetHeader className="sr-only">
          <SheetTitle>Navigation Menu</SheetTitle>
        </SheetHeader>
        <div className="min-h-full flex flex-col">
          <div className="flex-shrink-0">
            <MobileUserProfile user={user} getUserInitials={getUserInitials} />
          </div>

          <div className="flex-1 overflow-y-auto p-6 space-y-6">
            {user && (
              <>
                <MobileDashboardButton />
                <Separator />
              </>
            )}
            
            <MobileNavigationItems />

            {user && (
              <>
                <Separator />
                <MobileUserMenuItems />
                {isAdmin && (
                  <>
                    <Separator />
                    <MobileAdminMenuItems />
                  </>
                )}
              </>
            )}
          </div>

          <div className="flex-shrink-0">
            <MobileAuthSection user={user} signOut={signOut} />
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
};

const MobileMenu = (props: MobileMenuProps) => {
  return (
    <MobileMenuProvider user={props.user}>
      <MobileMenuContent {...props} />
    </MobileMenuProvider>
  );
};

export default MobileMenu;
